import { Dialog } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  HostListener,
  input,
  Input,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';

import { Observable } from 'rxjs';

import { env } from '@fantaz/shared/app-config';
// !FIXME: remove relative path
import { CurrentUser } from '@fantaz/shared/data-access/models';
import { Game } from '@fantaz/shared/data-access/models/src/lib/game';
import {
  CasualTournamentDetails,
  CasualTournamentEntity,
  EventSchedule,
  PrizeDetails,
  TournamentPass,
} from '@fantaz/shared/data-access/models/src/lib/tournament';
import { TimeComponents } from '@fantaz/shared/utils/date';
import { DialogVideoComponent } from '@fantaz/tournaments/ui/dialog-video';

import { buildImageSrcSet, getImagePublicId } from './utils';

interface ActionBarConfig {
  showJoinButton?: boolean;
  showPlayButton?: boolean;
  enabledPlayButton?: boolean | null | undefined;
  playButtonLoading?: boolean | null | undefined;
  playButtonProcessing?: boolean;
  showTryForFreeButton?: boolean;
  showShareButton?: boolean;
  showPlayButtonAnother?: boolean;
  isGameOverPage?: boolean;
  isEventPage?: boolean;
}

@Component({
  selector: 'fz-banner, [fzBanner]',
  templateUrl: './dynamic-banner.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class BannerComponent implements OnInit {
  details = input<CasualTournamentDetails>();
  ts = input<CasualTournamentEntity>();

  @Input()
  actionBarConfig?: ActionBarConfig;

  @Input()
  user: CurrentUser | null | undefined;

  @Input()
  playText?: string;

  @Input()
  hideMoreOptions? = false;

  @Input()
  tournament?: CasualTournamentEntity | undefined;

  @Input()
  tournamentDetails?: CasualTournamentDetails | undefined | null;

  @Input()
  eventScheduler?: EventSchedule | undefined | null;

  @Input()
  variant?: string = 'banner';

  @Input()
  isGameHistoryDetails = false;

  @Input()
  passForTournament?: TournamentPass;

  @Input()
  activePass?: TournamentPass;

  @Input()
  assetUrl? = '';

  @Input()
  timeZone?: string | null;

  @Input()
  timesPlayed?: number | null;

  useFloatingBanner = input<boolean>(false);

  hideTimeOnMobile = input(false);

  @Output()
  joinPress = new EventEmitter();

  @Output()
  playPress: EventEmitter<TournamentPass | null> =
    new EventEmitter<TournamentPass | null>();

  @Output()
  tryForFreePress = new EventEmitter();

  @Output()
  sharePress = new EventEmitter();

  @Output()
  playAnotherPress = new EventEmitter();

  customBreakpoint = '';

  public countdown$: Observable<TimeComponents>;

  showFloatingBanner = signal<boolean>(false);

  assetsUrl = env.baseURL;

  bannerImage = computed(() => {
    return buildImageSrcSet(
      {
        ...this.details(),
        ...this.details()?.metadata,
        ...this.tournament?.metadata,
      },
      this.tournament?.game || this.details()?.game || ({} as any),
    );
  });

  prizeImage = computed(() => {
    const details = this.details();
    const detailsForEventSchedule =
      details?.detailsForEventSchedule &&
      details?.detailsForEventSchedule.length > 0
        ? details?.detailsForEventSchedule[0]
        : null;

    const prizeDetails =
      detailsForEventSchedule?.prizeDetails &&
      detailsForEventSchedule?.prizeDetails.length > 0
        ? detailsForEventSchedule?.prizeDetails[0]
        : null;

    if (prizeDetails && prizeDetails.prizeImageAvatar) {
      if (typeof prizeDetails.prizeImageAvatar === 'string') {
        return getImagePublicId(prizeDetails.prizeImageAvatar);
      }

      if (Object.hasOwn(prizeDetails.prizeImageAvatar, 'url')) {
        return getImagePublicId(prizeDetails.prizeImageAvatar.url);
      }
    }

    return '';
  });

  constructor(private dialogService: Dialog) {}

  ngOnInit(): void {
    if (this.useFloatingBanner()) {
    }
  }

  /*
   * Action button visibility
   */

  get prizeImageOrientation(): 'landscape' | 'portrait' | '' {
    if (this.prizeDetail && this.prizeDetail.prizeImageLayout) {
      return this.prizeDetail.prizeImageLayout === 'landscape'
        ? 'landscape'
        : 'portrait';
    }

    return '';
  }

  get isCompleted(): boolean {
    return (
      (this.tournament && this.tournament.isComplete) ||
      (this.tournamentDetails && this.tournamentDetails.isCompleted) ||
      false
    );
  }

  get isExtended(): boolean {
    if (this.tournament?.metadata || this.tournamentDetails?.metadata) {
      const value = JSON.parse(
        ((this.tournament || this.tournamentDetails)?.metadata
          ?.extended as string) || 'false',
      );
      return value as boolean;
    }
    return false;
  }

  private getDefaultImage(
    game: Game,
    type: 'avatar' | 'background' = 'avatar',
  ): string {
    const result = game?.key.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    if (type === 'background') {
      return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-background.webp`;
    }

    return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
  }

  get gameImageUrl(): string {
    let game;
    if (this.tournament) {
      game = this.tournament.game;
    } else {
      game = this.tournamentDetails?.game;
    }

    return this.getDefaultImage(game as Game, 'avatar');
  }

  get backgroundImageUrl(): string {
    if (this.tournament) {
      if (this.tournament.tournamentFullSizeBanner) {
        return this.tournament.tournamentFullSizeBanner;
      }

      return this.getDefaultImage(this.tournament?.game, 'background');
    }

    if (this.tournamentDetails) {
      if (this.tournamentDetails.fullSizeBannerImage) {
        return this.tournamentDetails.fullSizeBannerImage as string;
      }

      return this.getDefaultImage(this.tournamentDetails?.game, 'background');
    }

    return '';
  }

  get game(): Game | null {
    if (this.tournament) {
      return this.tournament.game;
    }

    if (this.tournamentDetails) {
      return this.tournamentDetails.game;
    }

    return null;
  }

  get title(): string {
    if (this.tournament) {
      return this.tournament.name;
    }

    if (this.tournamentDetails) {
      return this.tournamentDetails.name;
    }

    return '';
  }

  get subtitle(): string {
    if (this.tournament) {
      return this.tournament.description;
    }

    if (this.tournamentDetails) {
      if (this.tournamentDetails.description) {
        return this.tournamentDetails.description;
      }

      if (
        this.tournamentDetails.metadata &&
        this.tournamentDetails.metadata?.gameDescriptionShort
      ) {
        return this.tournamentDetails.metadata?.gameDescriptionShort as string;
      }
    }

    return '';
  }

  get startTime(): Date | null | undefined {
    if (this.tournament) {
      if (typeof this.tournament.casualGameStartTime === 'string') {
        return new Date(this.tournament.casualGameStartTime);
      }
      return this.tournament.casualGameStartTime;
    }

    if (this.tournamentDetails && this.tournamentDetails.casualGameStartTime) {
      return new Date(this.tournamentDetails.casualGameStartTime);
    }

    return null;
  }

  get endTime(): Date | null | undefined {
    if (this.tournament) {
      return this.tournament.casualGameEndTime;
    }

    if (this.tournamentDetails && this.tournamentDetails.casualGameStartTime) {
      return new Date(this.tournamentDetails.casualGameEndTime);
    }

    return null;
  }

  get prizeDetail(): PrizeDetails | null | undefined {
    if (this.tournament) {
      return this.tournament.prizeDetails;
    }

    if (this.tournamentDetails) {
      if (this.tournamentDetails.lifeExperience) {
        const prizeDetails: PrizeDetails[] =
          this.tournamentDetails?.detailsForEventSchedule.reduce(
            (acc: any[], curr: EventSchedule) => acc.concat(curr.prizeDetails),
            [],
          );
        const prize = prizeDetails.find((p) => p.rank === 1);

        return prize as any;
      }
    }

    return null;
  }

  get entryAmount(): number {
    if (this.eventScheduler) {
      return this.eventScheduler.entryAmount;
    }

    if (this.tournament) {
      if (this.tournament.lifeExperience) {
        return this.tournament.eventSchedules[0].entryAmount;
      }
    }

    if (this.tournamentDetails) {
      if (this.tournamentDetails.lifeExperience) {
        const event = this.tournamentDetails.detailsForEventSchedule.find(
          (e) => {
            const prize = e.prizeDetails.find(
              (p) => p.prizeType === 'LIFE_EXPERIENCE',
            );
            if (prize) {
              return true;
            }

            return false;
          },
        );

        if (event) {
          return event.entryAmount;
        } else {
          console.error('Tournament error data');
        }
      }
    }

    return 0.0;
  }

  get actionBarOptions(): ActionBarConfig {
    return {
      // default options,
      showShareButton: true,
      // custom options
      ...this.actionBarConfig,
    };
  }

  get isComingSoon(): boolean {
    if (this.startTime) {
      if (this.startTime.getTime() > Date.now()) {
        return true;
      }
    }
    return false;
  }

  get isZboOnly(): boolean {
    return !!(
      this.tournament?.metadata?.tags?.includes('ZBO') ||
      this.tournamentDetails?.metadata?.tags?.includes('ZBO')
    );
  }

  get isLifeExperience(): boolean {
    if (this.tournament) {
      return this.tournament.lifeExperience;
    }

    if (this.tournamentDetails) {
      //
      return this.tournamentDetails.lifeExperience as boolean;
    }

    return false;
  }

  get isEnded(): boolean {
    if (this.endTime) {
      const date =
        typeof this.endTime === 'string'
          ? new Date(this.endTime)
          : this.endTime;
      if (date.getTime() < Date.now()) {
        return true;
      }
    }
    return false;
  }

  get vipVideo(): string | null {
    if (this.tournament) {
      if (
        this.tournament.lifeExperience &&
        this.tournament.metadata &&
        this.tournament.metadata.vipVideo
      ) {
        return this.tournament.metadata.vipVideo as string | null;
      }
    }

    if (this.tournamentDetails) {
      if (
        this.tournamentDetails.lifeExperience &&
        this.tournamentDetails.metadata &&
        this.tournamentDetails.metadata.vipVideo
      ) {
        return this.tournamentDetails.metadata.vipVideo as string;
      }
    }

    return null;
  }

  onJoinButtonPress() {
    this.joinPress.emit();
  }

  onPlayButtonPress() {
    this.playPress.emit(this.passForTournament as TournamentPass);
  }

  onShareButtonPress() {
    this.sharePress.emit();
  }

  onTryForFreePress() {
    this.tryForFreePress.emit();
  }

  onPlayAnotherPress() {
    this.playAnotherPress.emit();
  }

  onEvent(event: any) {
    //
  }

  openPromotionalVideo() {
    this.dialogService.open(DialogVideoComponent, {
      minWidth: '300px',
      data: {
        videoUrl: this.vipVideo,
      },
    });
  }

  @HostListener('window:scroll', ['$event']) onScrollEvent($event: any) {
    if (!this.useFloatingBanner()) {
      return;
    }

    const banner = document.querySelector('#banner_el') as HTMLElement;

    const rect = banner.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const isOutsideVertically = rect.bottom < 112 || rect.top > windowHeight;

    if (isOutsideVertically) {
      this.showFloatingBanner.set(true);
    } else {
      this.showFloatingBanner.set(false);
    }
  }
}
